import ResourcesDispatcher from '../config/ResourcesDispatcher';

// ============================================================================
// CURRENCY ENUMS
// ============================================================================
enum CurrencyText {
    EUR = 'eur',
    GBP = 'gbp',
    SEK = 'sek',
    USD = 'usd',
    AOA = 'aoa',
    GTQ = 'gtq',
    CHF = 'chf',
    NOC = 'noc',
    AED = 'aed',
    MXN = 'mxn',
    FUN = 'fun',
    BNS = 'bns'
}

enum CurrencySymbol {
    EUR = '€',
    GBP = '£',
    SEK = 'kr',
    USD = '$',
    AOA = 'kz',
    GTQ = 'Q',
    CHF = 'CHF',
    NOC = '',
    AED = 'DH',
    MXN = '$',
    FUN = 'FUN',
    BNS = 'BNS'
}

// ============================================================================
// CURRENCY CONFIGURATION
// ============================================================================
interface CurrencyConfig {
    symbol: string;
    divider: number;
}

const CURRENCY_MAP: Record<CurrencyText, CurrencyConfig> = {
    [CurrencyText.EUR]: { symbol: CurrencySymbol.EUR, divider: 100 },
    [CurrencyText.GBP]: { symbol: CurrencySymbol.GBP, divider: 100 },
    [CurrencyText.SEK]: { symbol: CurrencySymbol.SEK, divider: 10 },
    [CurrencyText.USD]: { symbol: CurrencySymbol.USD, divider: 100 },
    [CurrencyText.AOA]: { symbol: CurrencySymbol.AOA, divider: 100 },
    [CurrencyText.GTQ]: { symbol: CurrencySymbol.GTQ, divider: 100 },
    [CurrencyText.CHF]: { symbol: CurrencySymbol.CHF, divider: 100 },
    [CurrencyText.NOC]: { symbol: CurrencySymbol.NOC, divider: 100 },
    [CurrencyText.AED]: { symbol: CurrencySymbol.AED, divider: 100 },
    [CurrencyText.MXN]: { symbol: CurrencySymbol.MXN, divider: 100 },
    [CurrencyText.FUN]: { symbol: CurrencySymbol.FUN, divider: 100 },
    [CurrencyText.BNS]: { symbol: CurrencySymbol.BNS, divider: 100 }
};

// ============================================================================
// ANIMATION CONFIGURATION
// ============================================================================
const SPIN_BUTTON_CONFIG = {
    LOOP_DURATION: 450,
    STOP_DURATION: 750,
    ANGLE: 360,
    EASE_LOOP: 'Linear' as const,
    EASE_STOP: 'Cubic.easeOut' as const,
};
        

// ============================================================================
// UTILS CLASS
// ============================================================================
export default class Utils {
    /**
     * Waits for a specified time using Phaser's timer system
     */
    static wait(time: number, scene: any): Promise<void> {
        return new Promise((resolve) => {
            scene.time.addEvent({
                delay: time,
                callback: () => resolve()
            });
        });
    }

    /**
    * Checks if an array contains a specific value using strict equality
    */
    static includes(array: any[], value: any): boolean {
        for (let i = 0; i < array.length; i++) {
            if (array[i] === value) {
                return true;
            }
        }
        return false;
    }

    /**
     * Returns a random integer between min (inclusive) and max (inclusive)
     */
    static getRandomInt(min: number, max: number): number {
        const minCeil = Math.ceil(min);
        const maxFloor = Math.floor(max);
        return Math.floor(Math.random() * (maxFloor - minCeil + 1)) + minCeil;
    }

    /**
     * Generates reel positions starting from center and expanding left/right
     */
    static generateReelsPosition(numberOfReels: number, reelSize: { width: number; height: number }, border: number): { x: number; y: number }[] {
        let spacing = reelSize.width + border;
        const positions: { x: number; y: number }[] = [];

        for (let i = 0; i < numberOfReels; i++) {
            let offset = 0;

            switch(i) {
                case 0:
                    offset = 0;
                    break;
                case 1:
                    offset = 12;
                    break;
                case 2:
                    offset = 8;
                    break;
                case 3:
                    offset = 2;
                    break;
                case 4:
                    offset = 0;
                    break;
            }
            
            positions.push({
                x: (i * spacing) + offset,
                y: 0
            });
        }

        return ResourcesDispatcher.GAME_CONSTANTS.reelsPosition;
    }

    /**
     * Generates cell positions (3 visible + 2 extra cells)
     */
    static generateCellPositions(numCells: number, cellSize: { width: number; height: number }): { x: number; y: number }[] {
        const positions: { x: number; y: number }[] = [];
        const halfW = cellSize.width * 0.5;
        const halfH = cellSize.height * 0.5;

        for (let i = 0; i < numCells; i++) {
            const y = cellSize.height * (numCells - 2 - i) + halfH;
            positions.push({ x: halfW, y });
        }

        return positions;
    }

    /**
     * Gets the grid index for a cell, excluding extra cells (returns null for extra cells)
     */
    static getCellGridIndex(reelIndex: number, cellIndex: number, numOfCellsPerReel: number, numOfExtraCellsPerReel: number): number | null {
        const isExtraCell = cellIndex === 0 || cellIndex === numOfCellsPerReel + numOfExtraCellsPerReel - 1;

        if (isExtraCell) {
            return null;
        }

        return reelIndex * numOfCellsPerReel + cellIndex - 1;
    }

    /**
     * Formats a currency value with symbol and thousands separator
     * @TODO Make currency dynamic instead of hardcoded EUR
     */
    static formatCurrency(value: number, useSymbol: boolean = true, callbackFormatAmount?: (amount: number) => string): string {
        const currencyKey = CurrencyText.EUR; // TODO: Make this dynamic
        const { symbol, divider } = CURRENCY_MAP[currencyKey];
        const amount = value / divider;

        if (typeof callbackFormatAmount === 'function') {
            return callbackFormatAmount(amount);
        }

        const formatted = this.formatNumber(amount);
        return useSymbol ? `${symbol} ${formatted}` : formatted;
    }

    /**
     * Formats a number with thousands separator and decimal places
     */
    static formatNumber(value: number, decimals: number = 2): string {
        return value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    /**
     * Creates a rotation animation for the main spin button
     */
    static spinMainButtonRotation(mainButton: any, scene: any): Phaser.Tweens.Tween {
        const isFreeSpinPlaying = (window as any).isFreeSpinEntryPlaying;

        if (!isFreeSpinPlaying) {
            scene.soundManager.play(ResourcesDispatcher.SOUNDS.SPIN_MAIN_BUTTON);
        }

        return scene.tweens.add({
            targets: mainButton,
            angle: SPIN_BUTTON_CONFIG.ANGLE,
            duration: SPIN_BUTTON_CONFIG.LOOP_DURATION,
            repeat: -1,
            ease: SPIN_BUTTON_CONFIG.EASE_LOOP,
            onRepeat: () => {
                mainButton.angle = 0;
            },
            onStop: () => {
                this.createStopAnimation(mainButton, scene);
            }
        });
    }

    /**
     * Creates the stop animation for the spin button
     */
    private static createStopAnimation(mainButton: any, scene: any): void {
        scene.tweens.add({
            targets: mainButton,
            angle: SPIN_BUTTON_CONFIG.ANGLE,
            duration: SPIN_BUTTON_CONFIG.STOP_DURATION,
            ease: SPIN_BUTTON_CONFIG.EASE_STOP,
            onComplete: () => {
                scene.soundManager.stopFade(ResourcesDispatcher.SOUNDS.SPIN_MAIN_BUTTON);
            }
        });
    }

    /**
     * utils function that create a debug graphics object for visualizing positions and sizes of game elements
     * @param scene - the Phaser scene to which the graphics will be added
     * @param x - the x coordinate of the graphics object
     * @param y - the y coordinate of the graphics object
     * @param width - the width of the rectangle to be drawn
     * @param height - the height of the rectangle to be drawn
     * @param color - the color of the rectangle (default is red)
     * @param alpha - the transparency of the rectangle (default is 0.5)
     * @param parent - the parent container to which the graphics will be added (optional, if not provided, it will be added directly to the scene)
     */
    static createDebugGraphics(scene: Phaser.Scene, x: number, y: number, width: number, height: number, color: number = 0xff0000, alpha: number = 0.5, anchor: { x: number, y: number } = { x: 0.5, y: 0.5 }, parent?: Phaser.GameObjects.Container): Phaser.GameObjects.Rectangle {
        const graphics = scene.add.rectangle(x, y, width, height, color, alpha);
        graphics.setOrigin(anchor.x, anchor.y);
        if (parent) {
            parent.add(graphics);
        }
        return graphics;
    }
}